import React, { useState, useLayoutEffect, useMemo } from "react";
import cn from "classnames";
import style from "./common.module.scss";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { useTranslation } from "react-i18next";
import { useScreenSize } from "../../../hooks/useScreenSize";
import { getAmChartsLocale } from "../../../utils/chartDataProcessors";
import { useLanguage } from "../../../context/LanguageContext";
import { shortCompanyName } from "../../../utils/shortenName";

const ParticipationInTendersChart = ({ item }) => {
  const { t } = useTranslation();
  const { isMobileView } = useScreenSize();
  const { currentLanguage } = useLanguage();

  const colors = {
    winner: "#4E7CFF",
    count: "#22C0FF",
  };

  const processParticipationData = (data) => {
    if (!data || !Array.isArray(data)) return [];

    return data.map((client) => {
      const total = client.count;
      const winnerPercent = total > 0 ? Math.round((client.winner / total) * 100) : 0;
      const countPercent = 100;

      return {
        clientName: client.clientName,
        shortClientName: shortCompanyName(client.clientName),
        winner: client.winner,
        count: client.count,
        winnerPercent,
        countPercent,
        total,
      };
    });
  };

  const processedData = useMemo(() => {
    return processParticipationData(item);
  }, [item]);

  const maxCount = useMemo(() => {
    if (!processedData.length) return 100;
    return Math.max(...processedData.map((item) => item.total));
  }, [processedData]);

  useLayoutEffect(() => {
    let root = am5.Root.new("chartdiv-participation-tenders");
    root.setThemes([am5themes_Animated.new(root)]);

    const locale = getAmChartsLocale(currentLanguage);
    if (locale) {
      root.locale = locale;
    }

    root._logo.dispose();

    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        layout: root.verticalLayout,
        wheelY: "zoomX",
      }),
    );

    if (isMobileView) {
      chart.set("paddingBottom", 20);
    }
    chart.set("wheelable", true);

    let xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "clientName",
        renderer: am5xy.AxisRendererX.new(root, {
          cellStartLocation: 0.1,
          cellEndLocation: 0.9,
          minGridDistance: 30,
        }),
      }),
    );

    xAxis.data.setAll(processedData);

    xAxis.get("renderer").labels.template.adapters.add("text", (text, target) => {
      const dataItem = target.dataItem;
      if (dataItem) {
        return dataItem.dataContext.shortClientName;
      }
      return text;
    });

    let yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {
          strokeOpacity: 0.1,
          minGridDistance: 50,
        }),
        min: 0,
        max: Math.ceil(maxCount * 1.1),
      }),
    );

    const cursor = chart.set(
      "cursor",
      am5xy.XYCursor.new(root, {
        behavior: "zoomX",
        xAxis: xAxis,
      }),
    );

    xAxis.get("renderer").grid.template.setAll({
      strokeDasharray: [4, 4],
      strokeOpacity: 1,
      stroke: am5.color(0xe7e7f4),
    });

    xAxis.get("renderer").labels.template.setAll({
      fill: am5.color("#8E8E8E"),
      fontSize: 12,
      rotation: isMobileView ? -45 : 0,
    });

    yAxis.get("renderer").grid.template.setAll({
      strokeDasharray: [4, 4],
      strokeOpacity: 1,
      stroke: am5.color(0xe7e7f4),
    });

    yAxis.get("renderer").labels.template.setAll({
      fill: am5.color("#8E8E8E"),
      fontSize: 12,
    });

    yAxis.setAll({
      min: 0,
      max: Math.ceil(maxCount * 1.1),
      strictMinMax: true,
      numberFormat: "#",
    });

    function createSeries(field, name, color, countField, percentField, isCountSeries = false) {
      var series = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: name,
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: countField,
          categoryXField: "clientName",
          sequencedInterpolation: true,
          tooltip: am5.Tooltip.new(root, {
            pointerOrientation: "vertical",
            labelText: "{name}[/]\n{clientName}: {" + percentField + "}% ({" + countField + "})",
          }),
        }),
      );

      series.columns.template.setAll({
        width: am5.p100,
        strokeOpacity: 0,
        fill: am5.color(color),
        maxWidth: 80,
      });

      series.bullets.push(function () {
        return am5.Bullet.new(root, {
          locationX: 0.5,
          locationY: 1,
          sprite: am5.Label.new(root, {
            centerX: am5.p50,
            centerY: am5.p100,
            text: isCountSeries ? "{" + countField + "}" : "{" + countField + "}({" + percentField + "}%)",
            populateText: true,
            fill: isCountSeries ? am5.color("#333333") : am5.color("#4E7CFF"),
            fontSize: 11,
          }),
        });
      });

      series.data.setAll(processedData);
      series.appear();

      return series;
    }

    createSeries("count", t("analytics.submittedApplications"), colors.count, "count", "countPercent", true);
    createSeries("winner", t("analytics.wonTenders"), colors.winner, "winner", "winnerPercent", false);

    return () => {
      root.dispose();
    };
  }, [item, isMobileView, processedData, currentLanguage, maxCount]);

  return (
    <div className={style.content}>
      <div className={style.filter}>
        <span className={style.filter_title}>{t("analytics.tenderEfficiencyByCompany")}</span>
      </div>

      <div className={cn(style.area__namesMobile)}>
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          <div className={style.area_icon} style={{ backgroundColor: colors.count }}></div>
          <div className={style.area_type}>{t("analytics.submittedApplications")}</div>
        </div>
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          <div className={style.area_icon} style={{ backgroundColor: colors.winner }}></div>
          <div className={style.area_type}>{t("analytics.wonTenders")}</div>
        </div>
      </div>

      <div className={style.area}>
        <div id="chartdiv-participation-tenders" className={style.area_chart}></div>
        <div className={style.area_names}>
          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <div className={style.area_icon} style={{ backgroundColor: colors.count }}></div>
            <div className={style.area_type}>{t("analytics.submittedApplications")}</div>
          </div>
          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <div className={style.area_icon} style={{ backgroundColor: colors.winner }}></div>
            <div className={style.area_type}>{t("analytics.wonTenders")}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParticipationInTendersChart;
